import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowDownAZ, ArrowUpAZ, CalendarDays, Check, Clock3, Download, FileText, HelpCircle, Images, Info, Link2, LockKeyhole, Scale, ShieldCheck, Stethoscope } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { BottomNav } from '../components/BottomNav'
import { PetAvatar } from '../components/PetAvatar'
import { PublicHeader } from '../components/PublicHeader'
import { SponsoredPlacement } from '../components/SponsoredPlacement'
import { sharedRecordCopy } from '../content/sharedRecordCopy'
import { useI18n } from '../core/i18n'
import { initialAdSlots, initialFeatureFlags, initialNavigation, initialPageBlocks } from '../data/adminData'
import { loadSharedCase } from '../data/memberRepository'
import { loadPublicConfig, mergePlatformConfig } from '../data/platformRepository'
import type { CaseData, EditorBlock, MediaItem, Pet, PublicPlatformConfig, TimelineItem } from '../types'

function dateLabel(value: string, ko: boolean, withYear = true) {
  const date = value ? new Date(`${value.slice(0, 10)}T00:00:00+09:00`) : null
  if (!date || Number.isNaN(date.getTime())) return ko ? '미정' : 'Not set'
  return new Intl.DateTimeFormat(ko ? 'ko-KR' : 'en-US', { year: withYear ? 'numeric' : undefined, month: 'short', day: 'numeric' }).format(date)
}

function dateTimeLabel(value: string, ko: boolean) {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return ko ? '미정' : 'Not set'
  return new Intl.DateTimeFormat(ko ? 'ko-KR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)
}

function categoryLabel(item: TimelineItem, ko: boolean) {
  if (item.category === 'visit') return ko ? '병원 방문' : 'Clinic visit'
  if (item.category === 'medication') return ko ? '투약과 처치' : 'Medication'
  if (item.category === 'symptom') return ko ? '증상' : 'Symptom'
  if (item.category === 'adoption') return ko ? '입양' : 'Adoption'
  if (item.category === 'notice') return ko ? '보호 공고' : 'Shelter notice'
  return ko ? '생활 기록' : 'Home note'
}

function TimelineMedia({ block, media, ko }: { block: EditorBlock; media?: MediaItem; ko: boolean }) {
  if (!media?.src) return <div className="handoff-attachment-note"><Images /><span>{block.assetName || block.content}</span></div>
  if (media.type === 'video') return <figure className="handoff-inline-media"><video src={media.src} controls preload="metadata" playsInline /><figcaption>{block.assetName || media.title[ko ? 'ko' : 'en']}</figcaption></figure>
  return <figure className="handoff-inline-media"><img src={media.src} alt={block.assetName || media.title[ko ? 'ko' : 'en']} /><figcaption>{block.assetName || media.title[ko ? 'ko' : 'en']}</figcaption></figure>
}

function TimelineCard({ item, record, ko }: { item: TimelineItem; record: CaseData; ko: boolean }) {
  const copy = sharedRecordCopy[ko ? 'ko' : 'en']
  const blocks = item.blocks || []
  const mediaBlocks = blocks.filter((block) => block.assetId && ['image', 'video'].includes(block.assetKind || ''))
  const bodyBlocks = blocks.filter((block) => block.content && !block.type.startsWith('heading') && !block.assetId).slice(0, 4)
  return (
    <li className={`handoff-timeline-card severity-${item.severity}`}>
      <time><strong>{dateLabel(item.date, ko, false)}</strong><small>{item.time || ''}</small></time>
      <article>
        <div className="handoff-card-meta">
          <span>{categoryLabel(item, ko)}</span>
          {item.severity === 'critical' && <em><AlertTriangle />{copy.guardianConcern}</em>}
        </div>
        <h3>{item.title[ko ? 'ko' : 'en']}</h3>
        {mediaBlocks.length > 0 && <div className="handoff-media-grid">{mediaBlocks.map((block) => <TimelineMedia key={block.id} block={block} media={record.media.find((mediaItem) => mediaItem.id === block.assetId)} ko={ko} />)}</div>}
        {bodyBlocks.length ? bodyBlocks.map((block) => <p key={block.id}>{block.content}</p>) : <p>{item.description[ko ? 'ko' : 'en']}</p>}
        <small className="handoff-source"><ShieldCheck />{item.source.label[ko ? 'ko' : 'en']}</small>
      </article>
    </li>
  )
}

function PetSwitchVisual({ pet, record, ko }: { pet: Pet; record: CaseData; ko: boolean }) {
  const image = pet.profileImageSrc || record.media.find((item) => item.type === 'image' && item.petIds.includes(pet.id) && item.src)?.src
  if (image) return <img src={image} alt={`${pet.name[ko ? 'ko' : 'en']} ${sharedRecordCopy[ko ? 'ko' : 'en'].profileImage}`} />
  return <PetAvatar pet={pet} size="small" />
}

export function SharedRecordPage() {
  const { token = '' } = useParams()
  const { locale } = useI18n()
  const ko = locale === 'ko'
  const copy = sharedRecordCopy[ko ? 'ko' : 'en']
  const [record, setRecord] = useState<CaseData | null>(null)
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState('')
  const [sort, setSort] = useState<'asc' | 'desc'>('asc')
  const [selectedPet, setSelectedPet] = useState('')
  const [remoteConfig, setRemoteConfig] = useState<PublicPlatformConfig | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadSharedCase(token, controller.signal)
      .then((result) => {
        setRecord(result.case)
        setExpiresAt(result.expiresAt)
        setSelectedPet(result.case.pets[0]?.id || '')
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : '공유 기록을 열 수 없습니다.'))
    return () => controller.abort()
  }, [token])

  useEffect(() => {
    const controller = new AbortController()
    loadPublicConfig(controller.signal).then(setRemoteConfig).catch(() => undefined)
    return () => controller.abort()
  }, [])

  const timeline = useMemo(() => {
    const values = (record?.timeline || []).filter((item) => !selectedPet || item.petIds.includes(selectedPet))
    return [...values].sort((a, b) => {
      const result = `${a.date} ${a.time || ''}`.localeCompare(`${b.date} ${b.time || ''}`)
      return sort === 'asc' ? result : -result
    })
  }, [record, selectedPet, sort])

  const pet = record?.pets.find((item) => item.id === selectedPet) || record?.pets[0]
  const symptoms = (record?.symptoms || []).filter((item) => (!selectedPet || item.petIds.includes(selectedPet)) && item.status !== 'resolved')
  const questions = record?.questions.filter((item) => !item.checked && (!item.petIds?.length || item.petIds.includes(selectedPet))) || []
  const config = mergePlatformConfig(remoteConfig, { pageBlocks: initialPageBlocks, navigation: initialNavigation, adSlots: initialAdSlots, featureFlags: initialFeatureFlags })
  const managedCopy = config.pageCopy.shared[locale]

  if (error) return <div className="shared-record handoff-v2"><PublicHeader /><main className="shared-state"><LockKeyhole /><h1>{copy.unavailableTitle}</h1><p>{error}</p><small>{copy.unavailableHint}</small></main></div>
  if (!record || !pet) return <div className="shared-record handoff-v2"><PublicHeader /><main className="shared-state"><span className="loading-ring" /><h1>{copy.loadingTitle}</h1></main></div>

  const petMedia = record.media.find((item) => item.petIds.includes(pet.id) && item.src)
  const petVisual = pet.profileVideoSrc || pet.profileImageSrc || petMedia?.src
  const visualIsVideo = Boolean(pet.profileVideoSrc || (!pet.profileImageSrc && petMedia?.type === 'video'))
  const petDocuments = record.documents.filter((item) => !item.petIds.length || item.petIds.includes(pet.id))
  const petMediaItems = record.media.filter((item) => item.petIds.includes(pet.id))

  return (
    <div className="shared-record handoff-v2">
      <PublicHeader />
      <main>
        {record.pets.length > 1 && (
          <nav className="handoff-pet-switcher" aria-label={copy.petSwitcherLabel}>
            {record.pets.map((item) => {
              const active = item.id === pet.id
              return (
                <button type="button" key={item.id} aria-pressed={active} onClick={() => setSelectedPet(item.id)}>
                  <span className="handoff-switcher-photo"><PetSwitchVisual pet={item} record={record} ko={ko} /></span>
                  <span><strong>{item.name[ko ? 'ko' : 'en']}</strong><small>{active ? copy.selectedPet : item.estimatedAge[ko ? 'ko' : 'en']}</small></span>
                  {active && <Check aria-hidden="true" />}
                </button>
              )
            })}
          </nav>
        )}

        <section className="handoff-hero" id="overview">
          <div className="handoff-trust">
            <span><Stethoscope />{copy.guardianRecord}</span>
            <span><LockKeyhole />{copy.readOnly}</span>
          </div>

          <div className="handoff-identity">
            <div className="handoff-pet-media">
              {petVisual
                ? visualIsVideo
                  ? <video src={petVisual} controls preload="metadata" playsInline />
                  : <img src={petVisual} alt={`${pet.name[ko ? 'ko' : 'en']} ${copy.profileImage}`} />
                : <div><PetAvatar pet={pet} size="large" /><span>{copy.noProfileImage}</span></div>}
            </div>
            <div className="handoff-pet-copy">
              <span>{pet.species === 'cat' ? copy.cat : copy.pet}</span>
              <h1>{pet.name[ko ? 'ko' : 'en']}</h1>
              <dl>
                <div><dt>{copy.age}</dt><dd>{pet.estimatedAge[ko ? 'ko' : 'en']}</dd></div>
                <div><dt>{copy.sex}</dt><dd>{pet.sex[ko ? 'ko' : 'en']}</dd></div>
                <div><dt>{copy.adopted}</dt><dd>{dateLabel(pet.adoptionDate, ko)}</dd></div>
                <div><dt>{copy.registration}</dt><dd>{pet.registration || copy.unregistered}</dd></div>
              </dl>
            </div>
          </div>

          <section className="handoff-current-notes" aria-labelledby="current-notes-heading">
            <header><span>{managedCopy.eyebrow}</span><h2 id="current-notes-heading">{managedCopy.title}</h2><p>{managedCopy.body}</p></header>
            {symptoms.length
              ? <ul>{symptoms.map((symptom) => <li key={symptom.id}><strong>{symptom.title[ko ? 'ko' : 'en']}</strong><p>{symptom.detail[ko ? 'ko' : 'en']}</p></li>)}</ul>
              : <p className="handoff-current-empty">{copy.noCurrentNotes}</p>}
          </section>

          <dl className="handoff-meta">
            {record.clinicVisitDate && <div><dt><CalendarDays />{copy.scheduledVisit}</dt><dd>{dateLabel(record.clinicVisitDate, ko)}</dd></div>}
            <div><dt><Clock3 />{copy.lastEdited}</dt><dd>{dateTimeLabel(record.lastUpdatedAt, ko)}</dd></div>
            <div><dt><Link2 />{copy.expires}</dt><dd>{dateTimeLabel(expiresAt, ko)}</dd></div>
          </dl>
        </section>

        <div className="handoff-body">
          <section className="handoff-scan" aria-labelledby="page-contents-heading">
            <h2 id="page-contents-heading">{copy.pageContents}</h2>
            <dl>
              <div><dt>{copy.ongoingSymptoms}</dt><dd>{symptoms.length}{ko ? '건' : ''}</dd></div>
              <div><dt>{copy.questions}</dt><dd>{questions.length}{ko ? '건' : ''}</dd></div>
              <div><dt>{copy.attachments}</dt><dd>{petMediaItems.length + petDocuments.length}{ko ? '개' : ''}</dd></div>
            </dl>
          </section>

          <section className="handoff-timeline" id="timeline">
            <header>
              <div><span>{copy.timelineEyebrow}</span><h2>{managedCopy.sectionTitle}</h2><p>{sort === 'asc' ? managedCopy.sectionBody : copy.newestDescription}</p></div>
              <div className="sort-toggle">
                <button type="button" aria-pressed={sort === 'asc'} className={sort === 'asc' ? 'active' : ''} onClick={() => setSort('asc')}><ArrowDownAZ />{copy.oldest}</button>
                <button type="button" aria-pressed={sort === 'desc'} className={sort === 'desc' ? 'active' : ''} onClick={() => setSort('desc')}><ArrowUpAZ />{copy.newest}</button>
              </div>
            </header>
            {timeline.length ? <ol>{timeline.map((item) => <TimelineCard key={item.id} item={item} record={record} ko={ko} />)}</ol> : <div className="handoff-empty"><Info />{copy.timelineEmpty}</div>}
          </section>

          <SponsoredPlacement slots={config.adSlots} position="after-timeline" />

          <section className="handoff-collapsed-materials">
            <details id="media">
              <summary><Images /><span><strong>{copy.mediaHeading}</strong><small>{petMediaItems.length}{ko ? '개' : ''}</small></span></summary>
              <div className="handoff-source-media">{petMediaItems.length ? petMediaItems.map((item) => item.src ? <figure key={item.id}>{item.type === 'video' ? <video src={item.src} controls preload="metadata" playsInline /> : <img src={item.src} alt={item.title[ko ? 'ko' : 'en']} />}<figcaption>{item.title[ko ? 'ko' : 'en']}</figcaption></figure> : null) : <p>{copy.mediaEmpty}</p>}</div>
            </details>
            <details id="documents">
              <summary><FileText /><span><strong>{copy.documentsHeading}</strong><small>{petDocuments.length}{ko ? '개' : ''}</small></span></summary>
              <div className="handoff-document-list">{petDocuments.length ? petDocuments.map((document) => <article key={document.id}><FileText /><span><strong>{document.title[ko ? 'ko' : 'en']}</strong><small>{document.date ? dateLabel(document.date, ko) : ''}</small></span>{document.href ? <a href={document.href} target="_blank" rel="noreferrer"><Download />{copy.openFile}</a> : <em>{copy.fileUnavailable}</em>}</article>) : <p>{copy.documentEmpty}</p>}</div>
            </details>
            <details id="questions" open={questions.length > 0}>
              <summary><HelpCircle /><span><strong>{managedCopy.actionTitle}</strong><small>{questions.length}{ko ? '건' : ''}</small></span></summary>
              <div className="handoff-question-list">{questions.length ? <><p className="handoff-question-context">{managedCopy.actionBody}</p><ol>{questions.map((question) => <li key={question.id}><strong>{question.title[ko ? 'ko' : 'en']}</strong><p>{question.detail[ko ? 'ko' : 'en']}</p></li>)}</ol></> : <p>{copy.questionEmpty}</p>}</div>
            </details>
            <details>
              <summary><Scale /><span><strong>{copy.factsHeading}</strong></span></summary>
              <div className="handoff-facts">
                {pet.firstCondition[ko ? 'ko' : 'en'] && <p><strong>{copy.initialCondition}</strong><span>{pet.firstCondition[ko ? 'ko' : 'en']}</span></p>}
                {pet.feeding[ko ? 'ko' : 'en'] && <p><strong>{copy.usualFeeding}</strong><span>{pet.feeding[ko ? 'ko' : 'en']}</span></p>}
              </div>
            </details>
          </section>

          <SponsoredPlacement slots={config.adSlots} position="after-documents" />
          <footer className="handoff-disclaimer"><ShieldCheck /><div><strong>{copy.disclaimerTitle}</strong><p>{copy.disclaimerBody}</p></div></footer>
        </div>
        <SponsoredPlacement slots={config.adSlots} position="desktop-side" className="handoff-desktop-sponsored" />
      </main>
      <SponsoredPlacement slots={config.adSlots} position="above-bottom-nav" className="handoff-bottom-sponsored" />
      <BottomNav items={config.navigation} />
    </div>
  )
}
